// RPR Scraper — Redfin-style layout with 5 data-backed tabs.
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  LinearProgress,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { apiFetch } from '../helpers';

interface RprRow {
  _id: string;
  address?: string;
  fullAddress?: string;
  city?: string | null;
  state?: string | null;
  listingPrice?: number | null;
  beds?: number | null;
  baths?: number | null;
  sqft?: number | null;
  detailUrl?: string | null;
  scrapedAt?: string;
  pushStatus?: string | null;
  existingSource?: string | null;
  // enriched from ScrapedDeal join
  joinedAmv?: number | null;
  bofaAmv?: number | null;
  amvSource?: string | null;
  isDeal?: boolean;
  dealStage?: string | null;
  bofaStatus?: string | null;
}

interface StatusResponse {
  isRunning: boolean;
  manuallyStopped: boolean;
  currentState: string | null;
  currentCity: string | null;
  currentStateIndex: number;
  totalStates: number;
  totalScraped: number;
  completedStates: string[];
  cycleCount: number;
  lastActivity: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  startedAt: string | null;
}

interface TabCounts {
  total: number;
  withAmv: number;
  bofaQueue: number;
  redfinQueue: number;
  withoutAmv: number;
  alreadyExists: number;
  deals: number;
}

interface StatsResponse {
  total: number;
  byState?: { _id: string; count: number }[];
}

type ViewMode = 'all' | 'with_amv' | 'bofa_queue' | 'redfin_queue' | 'without_amv' | 'already_exists';

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');

export default function RprScraper() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [counts, setCounts] = useState<TabCounts>({ total: 0, withAmv: 0, bofaQueue: 0, redfinQueue: 0, withoutAmv: 0, alreadyExists: 0, deals: 0 });
  const [rows, setRows] = useState<RprRow[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ROWS_PER_PAGE = 50;
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [filterState, setFilterState] = useState('all');
  const [rowsLoading, setRowsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/rpr-scrape/status');
      setStatus(await res.json());
    } catch (e: any) { setError(e.message); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/rpr-scrape/stats');
      setStats(await res.json());
    } catch {}
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await apiFetch('/api/rpr-scrape/compare');
      const data = await res.json();
      if (data.ok) setCounts(data.counts);
    } catch {}
  }, []);

  const fetchRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const stateParam = filterState !== 'all' ? `&state=${filterState}` : '';
      const res = await apiFetch(`/api/rpr-scrape/properties?viewMode=${viewMode}&page=${page}&limit=${ROWS_PER_PAGE}${stateParam}`);
      const data = await res.json();
      if (data.ok !== false) {
        setRows(data.properties || []);
        setRowsTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (e: any) { setError(e.message); }
    finally { setRowsLoading(false); }
  }, [viewMode, filterState, page]);

  useEffect(() => {
    fetchStatus(); fetchStats(); fetchCounts();
    const id = setInterval(() => { fetchStatus(); fetchStats(); fetchCounts(); }, 5000);
    return () => clearInterval(id);
  }, [fetchStatus, fetchStats, fetchCounts]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleStart = async () => {
    setActionLoading(true); setError('');
    try { await apiFetch('/api/rpr-scrape/start', { method: 'POST' }); fetchStatus(); }
    catch (e: any) { setError(e.message); }
    finally { setActionLoading(false); }
  };

  const handleStop = async () => {
    setActionLoading(true); setError('');
    try { await apiFetch('/api/rpr-scrape/stop', { method: 'POST' }); fetchStatus(); }
    catch (e: any) { setError(e.message); }
    finally { setActionLoading(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all RPR progress? This will start from the beginning on next run.')) return;
    setActionLoading(true);
    try { await apiFetch('/api/rpr-scrape/reset', { method: 'POST' }); fetchStatus(); fetchStats(); }
    catch (e: any) { setError(e.message); }
    finally { setActionLoading(false); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Delete ALL RPR data (properties + pipeline entries + progress)? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const res = await apiFetch('/api/rpr-scrape/delete-all', { method: 'POST' });
      const data = await res.json();
      if (data.ok) {
        alert(`Deleted ${data.deleted} RPR properties + ${data.rprDealsDeleted || 0} pipeline entries.`);
        fetchStatus(); fetchStats(); fetchCounts(); fetchRows();
      }
    } catch (e: any) { alert('Failed: ' + (e?.message || 'Unknown error')); }
    finally { setDeleting(false); }
  };

  const isRunning = status?.isRunning || false;
  const completedStates = status?.completedStates?.length || 0;
  const totalStates = status?.totalStates || 50;
  const pct = totalStates > 0 ? Math.round((completedStates / totalStates) * 100) : 0;
  const fmtDate = (iso?: string | null) => iso ? new Date(iso).toLocaleString() : '—';

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
          RPR Properties
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stat cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="h5" fontWeight={700}>{counts.total.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">With AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">{counts.withAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">BofA Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#2563eb' }}>{counts.bofaQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Redfin Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#dc2626' }}>{counts.redfinQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Without AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="warning.main">{counts.withoutAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Already Exists</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#6b7280' }}>{counts.alreadyExists.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary">Cycle</Typography>
          <Typography variant="h5" fontWeight={700}>#{status?.cycleCount || 0}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {status?.currentState ? `At: ${status.currentState}` : isRunning ? 'Running...' : 'Idle'}
          </Typography>
        </Paper>
      </Box>

      {/* Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {!isRunning ? (
            <Button variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={handleStart} disabled={actionLoading} sx={{ fontWeight: 600 }}>
              Start
            </Button>
          ) : (
            <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={handleStop} disabled={actionLoading} sx={{ fontWeight: 600 }}>
              Stop
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { fetchStatus(); fetchStats(); fetchCounts(); fetchRows(); }}>
            Refresh
          </Button>
          <Button variant="outlined" color="warning" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={actionLoading || isRunning}>
            Reset Progress
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={handleDeleteAll} disabled={deleting || isRunning}>
            {deleting ? 'Deleting...' : 'Delete All'}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Last activity: {fmtDate(status?.lastActivity)}
          </Typography>
        </Stack>

        {isRunning && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {completedStates}/{totalStates} states — {(status?.totalScraped || 0).toLocaleString()} saved
              </Typography>
              {status?.currentCity && status?.currentState && (
                <Chip label={`Scraping: ${status.currentCity}, ${status.currentState}`} color="info" size="small" />
              )}
            </Stack>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        )}

        {status?.lastError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Last error ({fmtDate(status.lastErrorAt)}): {status.lastError}
          </Alert>
        )}
      </Paper>

      {/* Tabs + state filter */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => { if (v) { setViewMode(v); setPage(1); } }} size="small">
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700 }}>
            All ({counts.total.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="with_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            With AMV ({counts.withAmv.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="bofa_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            BofA Queue ({counts.bofaQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="redfin_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Redfin Queue ({counts.redfinQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="without_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Without AMV ({counts.withoutAmv.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="already_exists" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Already Exists ({counts.alreadyExists.toLocaleString()})
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>State</InputLabel>
          <Select value={filterState} label="State" onChange={(e) => { setFilterState(e.target.value); setPage(1); }}>
            <MenuItem value="all">All States</MenuItem>
            {(stats?.byState || []).map((s) => (
              <MenuItem key={s._id} value={s._id}>{s._id} ({s.count.toLocaleString()})</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Native dark-header table */}
      <div
        style={{
          overflowX: 'auto',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#111' }}>
            {viewMode === 'all' && `All Properties (${counts.total.toLocaleString()})`}
            {viewMode === 'with_amv' && `With AMV (${counts.withAmv.toLocaleString()})`}
            {viewMode === 'bofa_queue' && `BofA Queue (${counts.bofaQueue.toLocaleString()})`}
            {viewMode === 'redfin_queue' && `Redfin Queue (${counts.redfinQueue.toLocaleString()})`}
            {viewMode === 'without_amv' && `Without AMV (${counts.withoutAmv.toLocaleString()})`}
            {viewMode === 'already_exists' && `Already Exists (${counts.alreadyExists.toLocaleString()})`}
          </span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Showing {rows.length}{rowsTotal > rows.length ? ` of ${rowsTotal.toLocaleString()}` : ''} rows
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: '#111827', color: '#fff' }}>
              {['Address', 'State', 'List Price', 'AMV', 'Source', 'Deal?', 'Status'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    padding: '12px 14px',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsLoading && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </td>
              </tr>
            )}
            {!rowsLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  {viewMode === 'all' && 'No properties yet. Click "Start" to scrape from RPR.'}
                  {viewMode === 'with_amv' && 'No properties with AMV yet.'}
                  {viewMode === 'bofa_queue' && 'BofA queue is empty.'}
                  {viewMode === 'redfin_queue' && 'Redfin queue is empty — nothing waiting for Redfin fallback.'}
                  {viewMode === 'without_amv' && 'Nothing here — every property got an AMV from BofA or Redfin.'}
                  {viewMode === 'already_exists' && 'No duplicate properties detected.'}
                </td>
              </tr>
            )}
            {!rowsLoading && rows.map((p, i) => {
              const zebra = i % 2 === 0 ? '#ffffff' : '#f9fafb';
              const bg = p.isDeal ? '#f0fdf4' : zebra;
              const amv = p.joinedAmv || p.bofaAmv || 0;
              const hasAmv = amv > 0;
              const addressText = p.fullAddress || p.address || '—';
              return (
                <tr key={p._id} style={{ background: bg }}>
                  <td style={{ padding: 14, minWidth: 280, color: '#111', fontWeight: 600 }}>
                    {p.detailUrl ? (
                      <a href={p.detailUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
                        {addressText}
                      </a>
                    ) : addressText}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{p.state || '—'}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(p.listingPrice)}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: hasAmv ? '#7c3aed' : '#9ca3af', fontWeight: 600 }}>
                    {hasAmv ? fmt(amv) : '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.amvSource ? (
                      <Chip
                        label={p.amvSource}
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 600,
                          bgcolor: p.amvSource === 'bofa' ? '#ede9fe' : p.amvSource === 'redfin' ? '#dcfce7' : '#dbeafe',
                          color: p.amvSource === 'bofa' ? '#6d28d9' : p.amvSource === 'redfin' ? '#166534' : '#1d4ed8',
                        }}
                      />
                    ) : (<span style={{ color: '#9ca3af' }}>—</span>)}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.isDeal ? (
                      <Chip label="DEAL" size="small" sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 600, fontSize: 10 }} />
                    ) : (<span style={{ color: '#9ca3af' }}>—</span>)}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.pushStatus === 'already_exists' ? (
                      <Chip label={p.existingSource ? `exists (${p.existingSource})` : 'exists'} size="small"
                        sx={{ bgcolor: '#f3f4f6', color: '#6b7280', fontWeight: 600, fontSize: 10 }} />
                    ) : (
                      <Chip
                        label={p.bofaStatus || p.dealStage || (hasAmv ? 'done' : 'pending')}
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 600,
                          bgcolor:
                            p.bofaStatus === 'success' ? '#dcfce7' :
                            p.bofaStatus === 'no_data' ? '#fef3c7' :
                            p.bofaStatus === 'failed' ? '#fee2e2' :
                            p.dealStage === 'bofa_amv_pending' ? '#dbeafe' : '#f3f4f6',
                          color:
                            p.bofaStatus === 'success' ? '#166534' :
                            p.bofaStatus === 'no_data' ? '#92400e' :
                            p.bofaStatus === 'failed' ? '#991b1b' :
                            p.dealStage === 'bofa_amv_pending' ? '#1d4ed8' : '#6b7280',
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 0', borderTop: '1px solid #e5e7eb' }}>
            <Button size="small" disabled={page <= 1 || rowsLoading} onClick={() => setPage(1)} sx={{ textTransform: 'none', minWidth: 50 }}>First</Button>
            <Button size="small" disabled={page <= 1 || rowsLoading} onClick={() => setPage(p => Math.max(1, p - 1))} sx={{ textTransform: 'none', minWidth: 50 }}>Prev</Button>
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 600, minWidth: 120, textAlign: 'center' }}>
              Page {page.toLocaleString()} of {totalPages.toLocaleString()} · {rowsTotal.toLocaleString()} total
            </span>
            <Button size="small" disabled={page >= totalPages || rowsLoading} onClick={() => setPage(p => Math.min(totalPages, p + 1))} sx={{ textTransform: 'none', minWidth: 50 }}>Next</Button>
            <Button size="small" disabled={page >= totalPages || rowsLoading} onClick={() => setPage(totalPages)} sx={{ textTransform: 'none', minWidth: 50 }}>Last</Button>
          </div>
        )}
      </div>
    </Box>
  );
}
